/**
 * @file MedicalAssessmentDetailPage.tsx
 * @brief Doctor review workspace — Word-style MLR edit, sign, export.
 *
 * @details
 * Main canvas is the 13-section paper (`AssessmentReviewPanel`). Generate,
 * records import, and structured prognosis live in Tools so the page is not a
 * stack of forms. Claimant chrome + format/save row pin at the top; when the
 * shell topbar hides, the format row slides into that slot (08-09-2026).
 *
 * The manual builders that predated the intake inbox (Case Hub pull, session
 * link, statement builder, per-section synthesis) were removed on 13-09-2026:
 * clinic software pushes the case and mobile records it, so a doctor never
 * reached for them. (`AssessmentAdvancedPanel` and friends, git history.)
 *
 * @author DES
 * @date 02-09-2026
 * @updated 08-09-2026
 */

import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { ErrorState } from "@/components/feedback/ErrorState";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { formatDateTime } from "@/shared/utils/formatters";
import { cn } from "@/shared/theme/cn";
import { useMainScroll } from "@/shared/scroll/main-scroll-context";
import { useState, type CSSProperties } from "react";
import {
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import {
  downloadGripXml,
  downloadMlrPdf,
  useDeleteMedicalAssessment,
  useMedicalAssessmentDetail,
  useMlrSupplements,
} from "./api";
import { AssessmentReviewPanel } from "./components/AssessmentReviewPanel";
import { SessionSummaryPanel } from "./components/SessionSummaryPanel";
import { MLR_EDITOR_CHROME_ID } from "./components/MlrEditorChromePortal";
import { assessmentClaimantName } from "./assessmentListDisplay";

export function MedicalAssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { chromeCollapsed, headerHeight } = useMainScroll();
  const detail = useMedicalAssessmentDetail(id);
  const mlrSupplements = useMlrSupplements(id);
  const deleteAssessment = useDeleteMedicalAssessment();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const assessment = detail.data?.assessment;
  const statements = detail.data?.statements ?? [];
  const caseHub = assessment?.caseHub;

  const onExportGrip = async () => {
    if (!id) return;
    try {
      await downloadGripXml(id);
      toast({ title: "GRIP XML downloaded" });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Export failed";
      toast({ title: "GRIP export failed", description: message });
    }
  };

  /**
   * @param allowUnsigned the draft path — see the button below.
   */
  const onExportMlrPdf = async (allowUnsigned = false) => {
    if (!id) return;
    if (
      !allowUnsigned &&
      mlrSupplements.data?.statementOfTruthConfirmed !== true
    ) {
      toast({
        title: "Sign Statement of Truth first",
        description: "Complete the final sign-off section before exporting.",
      });
      return;
    }
    try {
      await downloadMlrPdf(id, allowUnsigned);
      toast({
        title: allowUnsigned ? "Draft PDF downloaded" : "MLR PDF downloaded",
        description: allowUnsigned
          ? "Stamped DRAFT on every page — it is not the report until it is signed."
          : undefined,
      });
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Export failed";
      toast({ title: "MLR PDF export failed", description: message });
    }
  };

  const onConfirmDelete = async () => {
    if (!id) return;
    try {
      await deleteAssessment.mutateAsync(id);
      toast({ title: "Assessment deleted" });
      navigate("/company/medical-assessment");
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Delete failed";
      toast({ title: "Could not delete assessment", description: message });
    }
  };

  if (detail.isLoading) {
    return (
      <div className={gridPageBleedClass("space-y-4 p-6")}>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (detail.isError || !assessment || !id) {
    return (
      <div className={gridPageBleedClass("space-y-4 p-6")}>
        <Button
          variant="ghost"
          className="w-fit"
          onClick={() => navigate("/company/medical-assessment")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Button>
        <ErrorState
          title="Assessment not found"
          error={detail.error ?? "Not found"}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <div className="sticky top-0 z-30" data-sticky-subnav>
          <div
            className="border-b border-r border-border/60 bg-background/80 backdrop-blur-md transition-transform duration-300 ease-out supports-[backdrop-filter]:bg-background/65 motion-reduce:transition-none"
            style={
              chromeCollapsed
                ? ({
                    transform: `translateY(-${headerHeight}px)`,
                  } satisfies CSSProperties)
                : undefined
            }
          >
            <div
              className={cn(
                "overflow-hidden transition-[max-height,opacity] duration-300 ease-out motion-reduce:transition-none",
                chromeCollapsed ? "max-h-0 opacity-0" : "max-h-40 opacity-100",
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 md:px-5">
                <div className="flex items-start gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate("/company/medical-assessment")}
                    aria-label="Back"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight">
                      {assessmentClaimantName(assessment)}
                    </h2>
                    <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                      Editable report · {formatDateTime(assessment.updatedAt)}{" "}
                      · <Badge variant="secondary">{assessment.status}</Badge>
                      {mlrSupplements.data?.statementOfTruthConfirmed ===
                      true ? (
                        <>
                          {" "}
                          · <Badge variant="secondary">Signed</Badge>
                        </>
                      ) : null}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* The consultation recording and its transcript live in
                      Sessions; the report is written from them, so it points
                      at them rather than leaving the doctor to search
                      (14-09-2026). */}
                  {assessment.sessionId ? (
                    <Button variant="outline" asChild>
                      <Link
                        to={`/company/sessions/${encodeURIComponent(
                          assessment.sessionId,
                        )}`}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Transcript
                      </Link>
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => void onExportMlrPdf()}
                    disabled={
                      mlrSupplements.data?.statementOfTruthConfirmed !== true
                    }
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download MLR PDF
                  </Button>
                  {/*
                      The draft path, and it stays SEPARATE from the button
                      above rather than unlocking it.

                      Reading the report on paper before signing is a real
                      need; letting the signed export happen without a
                      signature is not. The draft is stamped on every page and
                      named `…-DRAFT.pdf`, so the two cannot be confused in a
                      downloads folder. (18-09-2026)
                   */}
                  {mlrSupplements.data?.statementOfTruthConfirmed !== true ? (
                    <Button
                      variant="outline"
                      onClick={() => void onExportMlrPdf(true)}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Draft PDF
                    </Button>
                  ) : null}
                  <Button variant="outline" onClick={() => void onExportGrip()}>
                    <Download className="mr-2 h-4 w-4" />
                    GRIP XML
                  </Button>
                  <Button
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
            <div
              id={MLR_EDITOR_CHROME_ID}
              className={cn(
                "flex min-h-11 w-full flex-wrap items-center justify-between gap-3 px-4 py-2 md:px-5",
                !chromeCollapsed && "border-t border-border/50",
              )}
            />
          </div>
        </div>

        {/*
            ⚠️ OUTSIDE the sheet, and a sibling of it — not nested in the
            paper cell. Meeting Intelligence's Summary tab is GridBand /
            GridCell rows of the page grid; stuffing those bands inside a
            padded GridCell made the brief look like a card on the report.
            (19-09-2026)
         */}
        <SessionSummaryPanel assessmentId={id} />

        <GridCell padded={false}>
          <AssessmentReviewPanel
            assessmentId={id}
            assessment={assessment}
            statements={statements}
            caseHub={caseHub}
            onRefetch={() => void detail.refetch()}
          />
        </GridCell>
      </GridBlock>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              {assessment.title} and its AI drafts will be removed. The linked
              recording session stays in Sessions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAssessment.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteAssessment.isPending}
              onClick={(e) => {
                e.preventDefault();
                void onConfirmDelete();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
